import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';

export type EmailProvider = 'google' | 'microsoft';

export interface EmailProviderStatus {
  provider: EmailProvider;
  label: string;
  connected: boolean;
  email_address: string | null;
  connected_by_user_id: string | null;
  last_used_at: string | null;
  configured: boolean;
}

export interface EmailConnectionsStatus {
  feature_enabled: boolean;
  active_provider: EmailProvider | null;
  providers: EmailProviderStatus[];
}

export interface EmailConnectionStartResponse {
  authorization_url: string;
}

export const useEmailConnectionService = () => {
  const { getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  const getAuthHeader = useCallback(async () => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
    });
    return { Authorization: `Bearer ${token}` };
  }, [getAccessTokenSilently]);

  const getStatus = useCallback(async (): Promise<EmailConnectionsStatus> => {
    const authHeader = await getAuthHeader();
    const response = await fetch(`${apiUrl}/api/email-connections/status`, {
      method: 'GET',
      headers: { ...authHeader },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail ?? 'Failed to load email connections');
    }
    return response.json();
  }, [apiUrl, getAuthHeader]);

  const start = useCallback(
    async (provider: EmailProvider): Promise<EmailConnectionStartResponse> => {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/email-connections/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader },
        // return_origin lets the backend redirect back to whatever host we are
        // actually running on (localhost, dev.invirtus.co, etc.) after consent.
        body: JSON.stringify({ provider, return_origin: window.location.origin }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail ?? 'Failed to start connection');
      }
      return response.json();
    },
    [apiUrl, getAuthHeader],
  );

  const disconnect = useCallback(
    async (provider: EmailProvider): Promise<void> => {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${apiUrl}/api/email-connections/${provider}`, {
        method: 'DELETE',
        headers: { ...authHeader },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail ?? 'Failed to disconnect');
      }
    },
    [apiUrl, getAuthHeader],
  );

  return { getStatus, start, disconnect };
};
